// Money: invoices, payments, expenses, collections, the settlement — and file links — part of the API surface; views import from '../api' (index.js), never from here.
import { get, post, put, del, postForm } from './http';
// documentUrls points at download endpoints the work resources own.
import { retainersApi, jobsApi, publicationsApi } from './work';

export const invoicesApi = {
  create: (body) => post('/invoices', body),
  update: (id, body) => put(`/invoices/${id}`, body),
  remove: (id) => del(`/invoices/${id}`),

  // The photo or scan of the invoice that was sent to the client (migration 051). It
  // belongs to the INVOICE, not to a payment: an invoice is collected in several
  // installments and there is only ever one document behind them.
  files: (invoiceId, { archived = false } = {}) =>
    get(`/invoices/${invoiceId}/files${archived ? '?archived=1' : ''}`),
  uploadFile: (invoiceId, formData) => postForm(`/invoices/${invoiceId}/files`, formData),
  fileDownloadUrl: (fileId, { inline = false } = {}) =>
    get(`/invoices/files/${fileId}/download-url${inline ? '?inline=1' : ''}`),
  updateFile: (fileId, body) => put(`/invoices/files/${fileId}`, body),
  setFileArchived: (fileId, archived) => put(`/invoices/files/${fileId}/archived`, { archived }),
  removeFile: (fileId) => del(`/invoices/files/${fileId}`),
};

// Every stored file that belongs to a client, already nested client → retainer → job.
// ONE read for the whole screen: the server assembles the tree, because doing it here
// would mean a request per client per file table.
//
// One GET and nothing else, which is the whole resource. The screen views and downloads;
// a contract is still uploaded on its retainer or its job, a scan on its charge, a
// clipping on its publication.
export const clientDocumentsApi = {
  tree: ({ archived = false } = {}) => get(`/client-documents${archived ? '?archived=1' : ''}`),
};

// A row from that tree, back to the endpoint that can open it — keyed by the `kind` the
// server stamps on every file (see server/api/rest/clientDocuments/files.js).
//
// THE SAME DEVICE `agreementApis` (work.js) USES, and for the same reason. Four kinds of
// file are listed in one table with one pair of actions on each, and they differ in
// nothing but which signed-URL endpoint answers for them. That difference belongs in a
// lookup, not in a branch inside the component — and crucially NOT in a new endpoint:
// every one of these already existed and is already authorized, so this screen adds no
// file handling at all. FileActions takes the function straight as its `getUrl`.
// kind → the endpoint that domain already had. A row carries the `kind` the server
// stamped on it, and this is the one place it becomes a URL — the client-documents screen
// builds no file handling of its own.
//
// ⚠ A KIND MISSING FROM THIS MAP IS A ROW THAT CAN BE DISPLAYED AND NEVER OPENED, and
// that is exactly what happened: `job_work_file` (migration 064) was a fifth kind with no
// entry here and no screen reading it, so a content writer's uploads were stored in the
// database and in R2 and were visible nowhere. Every kind in
// `clientDocuments/files.js KIND` needs a line here.
export const documentUrls = {
  retainer_agreement: retainersApi.agreementUrl,
  job_agreement: jobsApi.agreementUrl,
  invoice_file: invoicesApi.fileDownloadUrl,
  publication_file: publicationsApi.downloadUrl,
  job_work_file: jobsApi.workFileUrl,
};

export const paymentsApi = {
  create: (body) => post('/payments', body),
  // The installments recorded against one charge — the list the undo is offered from.
  byInvoice: (invoiceId) => get(`/invoices/${invoiceId}/payments`),
  // UNDOING A PAYMENT RECORDED BY MISTAKE: what it would move in the gap, per partner by
  // name, and then the deletion itself. There is no edit — correcting is delete and record.
  removalPreview: (id) => get(`/payments/${id}/removal-preview`),
  remove: (id) => del(`/payments/${id}`),
};

export const expensesApi = {
  listOffice: () => get('/expenses/office'),
  // One expense in the lists' shape — the expense dialog re-reading its charge in place.
  get: (id) => get(`/expenses/${id}`),
  // EVERY expense the business has, in the half the screen is asking for: what is still
  // owed (the default) or the archive of what has been paid. Two fetches rather than one
  // payload split in the browser — the paid half grows every month forever while the
  // unpaid list stays short, and the two are ordered by different dates (the cost's own
  // date, and the date the money left).
  //
  // The response carries its own `unpaid` tally in BOTH halves. That is what the red dot
  // reads, never the rows on screen, so opening the archive cannot make it go quiet while
  // money is still owed.
  overview: (paid) => get(`/expenses/overview?paid=${paid ? 1 : 0}`),
  // What the business owes people who bought something for it with their own money
  // (migration 061). Reporting only, and structurally outside the partner gap: every row
  // behind it is unpaid, which is exactly the set the settlement excludes.
  owedToStaff: () => get('/expenses/owed-to-staff'),
  create: (body) => post('/expenses', body),
  update: (id, body) => put(`/expenses/${id}`, body),
  remove: (id) => del(`/expenses/${id}`),
  // Settling an unpaid expense: who paid is the only new fact, so this is a POST of that
  // rather than a full update — the same shape as recording a payment. `payers` is
  // utils/payers `payersPayload`: `{ paid_by_partner_id }` for one partner, or
  // `{ payers: [{ partner_id, amount }] }` for both (migration 069). No date: the server
  // stamps the day it is marked paid.
  pay: (id, payers) => post(`/expenses/${id}/pay`, payers),
  // Raising the charge on a PENDING client-borne cost — a price never set, or one priced
  // before charges were raised at entry. One call, one transaction on the server: any
  // topic conversion, the invoice and the link to the cost. Returns the invoice, so its
  // scan can be attached next. (A priced cost is charged by `create`/`update` above.)
  raiseCharge: (id, body) => post(`/expenses/${id}/charge`, body),
};

// Read-only: the balance is a standing statement of the gap between the partners, not
// a ledger. The partners never transfer money to each other, so there is nothing to
// record and no history to list (migration 049).
// The one money screen: every charge, grouped by income unit — what has been billed and
// is still owed, and what has not been billed yet but should be. Recording an invoice and
// recording a payment keep their own endpoints (invoicesApi / paymentsApi); this screen
// performs those acts, it does not redefine them.
export const collectionsApi = {
  list: () => get('/collections'),
  // What recording this charge would MOVE, before anything is saved: the job's costs
  // leaving its retainer's profit, with the real figures. A read — it writes nothing.
  conversionPreview: (expenseId) => get(`/collections/conversion/${expenseId}`),
  // The same, keyed by the JOB — for the expense form, whose cost may not exist yet.
  // `expenseId` names a stored row being edited (setting the price on a pending cost).
  conversionPreviewForJob: (jobId, expenseId) =>
    get(`/collections/conversion/job/${jobId}${expenseId ? `?expense=${expenseId}` : ''}`),
  // "Not billing this month" — a record of the decision, which also closes the
  // missing-invoice alert for that period.
  skipMonth: (retainerId, body) => post(`/collections/retainers/${retainerId}/skip`, body),
  unskipMonth: (retainerId, month) => del(`/collections/retainers/${retainerId}/skip/${month}`),
};

export const settlementsApi = {
  balance: () => get('/settlements/balance'),
  // What money actually moved, month by month. Its own request: it grows with every
  // month the business runs, and the balance above never needs it.
  monthly: () => get('/settlements/monthly'),
};
